import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, ComponentPolicy, field, ObjectManager, property } from '../../../concepts/objects';
import { Colors } from '../../../ui';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, PotentialInteraction } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { PreferredTextColor, TextAlignment } from '../../canvas/entities/TextConventions';
import { CommandInterpreter } from '../../canvas/extensibility/CommandInterpreter';
import { CommandScript } from '../../canvas/extensibility/CommandScript';
import { SimpleTextField } from '../../canvas/fields/SimpleTextField';
import { buttonPropertyLayout } from './ButtonPropertyLayouts';

const invocation = defineCommand(
    'buttonInvoke',
    'Button Invoke',
    (r, [id]: [string]) => {

        const interpreter = r.call(CommandInterpreter);
        const objects = r.call(ObjectManager);

        const self = objects.get(id, Button);
        const script = CommandScript.parse(self.commandScript);
        const [ok, error] = interpreter.verify(script);

        if (ok) {
            interpreter.exec(script);
        }
        else {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    },
    [],
);

const mutation = defineCommand(
    'buttonMutate',
    'Button Mutate',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const buttonDef = {
    name: 'Button',
    implements: [Physical, Resizable, Snappable, PreferredTextColor],
    invocation,
    mutation,
    propertyLayout: buttonPropertyLayout
};

const buttonPolicy = ComponentPolicy.noChildren('Button');

class ButtonContent extends SimpleTextField {

    // Override:
    public readonly fontSize = 0;
    public readonly fontFamily = 'Cabin';
    public readonly fontColor = 'inherit';
    public readonly fontWeight = 500;
    public readonly textAlignH = TextAlignment.middle;
    public readonly textAlignV = TextAlignment.middle;

    @property({ type: 'string', default: 'My Button' })
    public value!: string;

    public get boundary() {
        return true;
    }
}

@component(buttonDef)
export class Button extends RectangularPhysical implements Snappable, PreferredTextColor {

    @property({ type: 'string', default: '' })
    public commandScript!: string;

    @field({ type: ButtonContent, valueOnly: true })
    public content!: string;

    @property({ type: 'string', default: '#ffffff', style: true })
    public background!: string;

    public get boundary() {
        return true;
    }

    public get minWidth() {
        return 10;
    }

    public get minHeight() {
        return 10;
    }

    public get shouldSnap() {
        return true;
    }

    protected get policy() {
        return buttonPolicy;
    }

    public testInteraction({ type, mpt, selected }: PotentialInteraction): [boolean, string] {
        if (this.localBounds.contains(this.toRelative(mpt))) {
            if (type !== 'invoke' || !selected) {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    //
    // PreferredTextColor
    //

    public getPreferredTextColor(): string {
        return Colors.contrastTextColorFor(this.background);
    }
}
